<template>
  <page-list
    :list="list"
    :columns="columns"
    :group-actions="groupActions"
    :single-actions="singleActions"
    :export-data-options="exportDataOptions" />
</template>

<script>
import * as R from 'ramda'
import {
  getNameDescriptionTableColumn,
  getEnabledTableColumn,
  getTimeTableColumn,
} from '@/utils/common/tableColumn'
import { getEnabledSwitchActions } from '@/utils/common/tableActions'
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import { getCreatedAtFilter } from '@/utils/common/tableFilter'

export default {
  name: 'EndpointList',
  mixins: [WindowsMixin, ListMixin],
  props: {
    id: String,
    getParams: {
      type: Object,
    },
  },
  data () {
    return {
      urlRules: [
        {
          required: true,
          message: this.$t('system.text_175'),
          transform: val => val && R.trim(val),
        },
        {
          validator: this.$validate('url'),
        },
      ],
      list: this.$list.createList(this, {
        id: this.id,
        resource: 'endpoints',
        apiVersion: 'v1',
        getParams: this.getParam,
        filterOptions: {
          name: {
            label: this.$t('system.text_101'),
            filter: true,
            formatter: val => {
              return `name.contains("${val}")`
            },
          },
          created_at: getCreatedAtFilter(),
        },
      }),
      exportDataOptions: {
        items: [
          { label: 'ID', key: 'id' },
          { label: this.$t('system.text_101'), key: 'name' },
          { label: this.$t('system.text_176'), key: 'url' },
          { label: this.$t('common.createdAt'), key: 'created_at' },
        ],
        getParams: () => {
          return {
            interface: 'console',
          }
        },
      },
      columns: [
        getNameDescriptionTableColumn({
          onManager: this.onManager,
          formRules: [
            { required: true, message: this.$t('system.text_168') },
          ],
        }),
        getEnabledTableColumn(),
        {
          field: 'url',
          title: this.$t('system.text_176'),
          minWidth: 140,
          showOverflow: 'ellipsis',
          slots: {
            default: ({ row }, h) => {
              return [
                h('list-body-cell-wrap', {
                  props: {
                    row,
                    field: 'url',
                    label: this.$t('system.text_176'),
                    copy: true,
                    edit: true,
                    hideField: true,
                    onManager: this.onManager,
                    list: this.list,
                    formRules: this.urlRules,
                  },
                }, [<help-link href={ row.url } />]),
              ]
            },
          },
        },
        getTimeTableColumn(),
      ],
      groupActions: [
        {
          label: this.$t('system.text_128'),
          permission: 'endpoints_create',
          action: () => {
            this.createDialog('EndpointCreateDialog', {
              success: () => {
                this.refresh()
              },
            })
          },
          meta: () => {
            return {
              buttonType: 'primary',
            }
          },
        },
        {
          label: this.$t('common.batchAction'),
          actions: () => {
            return [
              ...getEnabledSwitchActions(this, undefined, ['endpoints_perform_enable', 'endpoints_perform_disable'], {
                actions: [
                  () => {
                    this.onManager('batchUpdate', {
                      id: this.list.selected,
                      managerArgs: {
                        data: {
                          enabled: true,
                        },
                      },
                    })
                  },
                  () => {
                    this.onManager('batchUpdate', {
                      id: this.list.selected,
                      managerArgs: {
                        data: {
                          enabled: false,
                        },
                      },
                    })
                  },
                ],
                metas: [
                  () => {
                    const allDisable = this.list.selectedItems.every(item => !item.enabled)
                    return {
                      validate: allDisable,
                    }
                  },
                  () => {
                    const allEnable = this.list.selectedItems.every(item => item.enabled)
                    return {
                      validate: allEnable,
                    }
                  },
                ],
              }),
              {
                label: this.$t('system.text_129'),
                permission: 'endpoints_delete',
                action: () => {
                  this.createDialog('DeleteResDialog', {
                    vm: this,
                    data: this.list.selectedItems,
                    columns: this.columns,
                    onManager: this.onManager,
                    title: this.$t('system.text_129'),
                    name: this.$t('dictionary.endpoint'),
                  })
                },
                meta: () => {
                  const ret = {
                    validate: true,
                    tooltip: null,
                  }
                  if (this.list.selectedItems.some(item => !item.can_delete)) {
                    ret.validate = false
                    return ret
                  }
                  return ret
                },
              },
            ]
          },
          meta: () => {
            return {
              validate: this.list.selected.length,
            }
          },
        },
      ],
      singleActions: [
        ...getEnabledSwitchActions(this, undefined, ['endpoints_perform_enable', 'endpoints_perform_disable'], {
          actions: [
            (obj) => {
              this.onManager('update', {
                id: obj.id,
                managerArgs: {
                  data: {
                    enabled: true,
                  },
                },
              })
            },
            (obj) => {
              this.onManager('update', {
                id: obj.id,
                managerArgs: {
                  data: {
                    enabled: false,
                  },
                },
              })
            },
          ],
          meta: [
            (obj) => {
              let tooltip
              if (!obj.enabled) tooltip = this.$t('system.text_177')
              return {
                validate: !obj.enabled,
                tooltip,
              }
            },
            (obj) => {
              let tooltip
              if (obj.enabled) tooltip = this.$t('system.text_178')
              return {
                validate: obj.enabled,
                tooltip,
              }
            },
          ],
        }),
        {
          label: this.$t('system.text_129'),
          permission: 'endpoints_delete',
          action: (obj) => {
            this.createDialog('DeleteResDialog', {
              vm: this,
              data: [obj],
              columns: this.columns,
              title: this.$t('system.text_129'),
              onManager: this.onManager,
              name: this.$t('dictionary.endpoint'),
            })
          },
          meta: (obj) => this.$getDeleteResult(obj),
        },
      ],
    }
  },
  created () {
    this.list.fetchData()
  },
  methods: {
    getParam () {
      const ret = {
        ...this.getParams,
        interface: 'console',
        details: true,
      }
      return ret
    },
  },
}
</script>
